import { useMemo } from 'react';
import Container from 'components/atomic/atoms/container';
import { CarouselVariant } from './carousel-variant';
import { BentoVariant } from './bento-variant';
import type { GalleryProps } from './types';
import { useGalleryTestOverride } from './use-gallery-test-override';

export type { GalleryProps, GalleryData, GalleryVariant } from './types';

/**
 * Gallery Organism
 * 
 * Generic image gallery with multiple layout variants.
 * Domain-agnostic - no real estate specific logic.
 * 
 * Variants:
 * - carousel: Mobile swiper
 * - bento-grid: Desktop mosaic with adaptive layouts (0-7+ images)
 * 
 * Slots:
 * - badgesSlot: Overlay content (top-left)
 * - counterSlot: Image counter (carousel only)
 * - seeAllSlot: "See All" button (bento only)
 * - mapSlot: Map component (bento only, shown when images < 7)
 * - placeholderSlot: Placeholder when no images (bento only)
 * - overlay: Full overlay content (e.g., compliance)
 * 
 * 🧪 TEMPORARY: Supports URL override for testing: ?gallery_test_count=N
 */
export function Gallery({
  data,
  variant,
  onImageClick,
  onSlideChange,
  onSeeAllClick,
  blur,
  badgesSlot,
  counterSlot,
  seeAllSlot,
  mapSlot,
  placeholderSlot,
  overlay,
  shareSlot,
  className,
}: GalleryProps) {
  // 🧪 TEMPORARY: Allow URL override for testing different image counts
  const testOverride = useGalleryTestOverride();
  
  // Apply override to data if active
  const displayData = useMemo(() => {
    if (testOverride === undefined) return data;
    
    const actualCount = data.images?.length || 0;
    const overrideCount = Math.min(testOverride, actualCount);
    
    return {
      ...data,
      images: data.images.slice(0, overrideCount),
      total: overrideCount,
    };
  }, [data, testOverride]);

  return (
    <Container
      className={className}
      sx={{ position: 'relative' }}
    >
      {variant === 'bento-grid' ? (
        <BentoVariant
          data={displayData}
          onImageClick={onImageClick}
          onSeeAllClick={onSeeAllClick}
          blur={blur}
          badgesSlot={badgesSlot}
          seeAllSlot={seeAllSlot}
          mapSlot={mapSlot}
          placeholderSlot={placeholderSlot}
        />
      ) : (
        <CarouselVariant
          data={displayData}
          onImageClick={onImageClick}
          onSlideChange={onSlideChange}
          blur={blur}
          badgesSlot={badgesSlot}
          counterSlot={counterSlot}
          placeholderSlot={placeholderSlot}
          shareSlot={shareSlot}
        />
      )}
      {overlay}
    </Container>
  );
}

export default Gallery;
